/*******************************************************************************
 * Copyright     : Copyright (c)  2020 Honeywell Inc. All rights reserved.
 *                 This work contains trade secrets and confidential materials
 *                 of Honeywell Inc., and its use or disclosure in whole or in
 *                 part without express written permission of Honeywell Inc.
 *                 is prohibited.
 *
 * Name          : APMSettingsSlice.ts
 *
 * Project Title : APM Settings Tool
 *
 * Description   : Redux Slice for APM Settings state
 *
 *
 * Company       : Honeywell Technology Solutions , Hyderabad
 *
 * @Author       : H422337
 *
 * @Date         : Feb 03,2022
 *
 * @Version      : Version 1.0
 *
 * By            :
 * On            :
 * Description   :
 *
 *
 ******************************************************************************/

import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { apiCallBegan, apiVersion, apmURLs } from "api";
import {
  EnumApmSettingsWorkFlow,
  IApmWorkFlowState,
} from "models/configuration";
import { AppThunk, RootState } from "stores/GlobalStore";
import {
  setNextDetailsData,
  setSavedDetailsData,
} from "stores/AircraftDetailsStore";
import {
  APMSettingField,
  ISavedNewAPMFileDetails,
} from "models/aircraftDetails";

export const initialState: IApmWorkFlowState = {
  workFlow: {
    currentWorkFlowType: EnumApmSettingsWorkFlow.History,
    currentWorkFlowStep: 0,
  },
  selectedApmRecordLoader: false,
  currentApmFileRecord: null,
};

const ApmWorkFlowSlice = createSlice({
  name: "ApmWorkFlow",
  initialState,
  reducers: {
    setCurrentWorkFlowType(
      apmWorkFlowState: IApmWorkFlowState,
      action: PayloadAction<EnumApmSettingsWorkFlow>
    ) {
      apmWorkFlowState.workFlow.currentWorkFlowType = action.payload;
    },
    setCurrentWorkFlowStep(
      apmWorkFlowState: IApmWorkFlowState,
      action: PayloadAction<number>
    ) {
      apmWorkFlowState.workFlow.currentWorkFlowStep = action.payload;
    },
    beginSelectedApmRecordLoader(apmWorkFlowState: IApmWorkFlowState) {
      apmWorkFlowState.selectedApmRecordLoader = true;
    },
    setSelectedApmRecordData(
      apmWorkFlowState: IApmWorkFlowState,
      action: PayloadAction<ISavedNewAPMFileDetails>
    ) {
      apmWorkFlowState.currentApmFileRecord = action.payload;
      apmWorkFlowState.selectedApmRecordLoader = false;
    },
    resetSelectedApmRecord(apmWorkFlowState: IApmWorkFlowState) {
      apmWorkFlowState.selectedApmRecordLoader = false;
      apmWorkFlowState.currentApmFileRecord = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(
        setNextDetailsData,
        (apmWorkFlowState: IApmWorkFlowState, action) => {
          apmWorkFlowState.workFlow.currentWorkFlowStep = 4;
          apmWorkFlowState.currentApmFileRecord = action.payload;
        }
      )
      .addCase(
        setSavedDetailsData,
        (apmWorkFlowState: IApmWorkFlowState, action) => {
          apmWorkFlowState.currentApmFileRecord = action.payload;
        }
      );
  },
});

export const {
  setCurrentWorkFlowType,
  setCurrentWorkFlowStep,
  setSelectedApmRecordData,
  beginSelectedApmRecordLoader,
  resetSelectedApmRecord,
} = ApmWorkFlowSlice.actions;
export const apmWorkFlowReducer = ApmWorkFlowSlice.reducer;

export const fetchSelectedApmRecord =
  (historyId: number): AppThunk =>
  async (dispatch) => {
    const url = apiVersion.v1 + apmURLs.APM_SETTINGS;
    const params = { historyId: historyId };

    dispatch(
      apiCallBegan({
        url,
        params,
        onStart: beginSelectedApmRecordLoader.type,
        onSuccess: setSelectedApmRecordData.type,
        onError: resetSelectedApmRecord.type,
        showGlobalError: true,
      })
    );
  };

export const selectCurrentWorkFlowType = (
  state: RootState
): EnumApmSettingsWorkFlow => state.apmWorkFlow.workFlow.currentWorkFlowType;
export const selectCurrentWorkFlowStep = (state: RootState): number =>
  state.apmWorkFlow.workFlow.currentWorkFlowStep;
export const selectCurrentApmFileRecord = (
  state: RootState
): ISavedNewAPMFileDetails | null => state.apmWorkFlow.currentApmFileRecord;
export const selectCurrentApmFileRecordApmSettings = (
  state: RootState
): APMSettingField[] | null | undefined =>
  state.apmWorkFlow.currentApmFileRecord?.apmSettings;
export const selectSelectedApmRecordLoader = (state: RootState): boolean =>
  state.apmWorkFlow.selectedApmRecordLoader;
